import { Skeleton, useMediaQuery } from "@mui/material";
import styles from "./enquirylist.module.css";

/**
 * Skeleton loader for the Enquiry List page.
 *
 * The real page renders an 11-column table, which doesn't fit well on
 * small screens. This loader mirrors the search bar + wide table on
 * desktop, but falls back to a stacked card skeleton on mobile so the
 * loading state doesn't imply a layout that will force horizontal
 * scrolling once data lands.
 *
 * Usage in enquirylist.jsx:
 *   import EnquiryListLoader from "./EnquiryListLoader";
 *   ...
 *   {contentLoading && <EnquiryListLoader />}
 */
const EnquiryListLoader = ({ rows = 8 }) => {
    const isMobile = useMediaQuery("(max-width:768px)");

    return (
        <>
            {/* Search bar */}
            <div className={styles.FilterBar}>
                <Skeleton variant="rounded" height={40} width="100%" />
            </div>

            {isMobile ? (
                <div className={styles.MobileCards || ""}>
                    {Array.from({ length: rows }).map((_, i) => (
                        <EnquiryCardSkeleton key={`ecard-${i}`} />
                    ))}
                </div>
            ) : (
                <EnquiryTableSkeleton rows={rows} />
            )}
        </>
    );
};

const EnquiryTableSkeleton = ({ rows }) => {
    const columns = [
        "Booking ID",
        "Item ID",
        "Product",
        "Paid",
        "Status",
        "Pickup Driver",
        "Delivery Driver",
        "Recipient",
        "Address",
        "Phone",
        "Container",
    ];

    return (
        <div className={styles.TableCard}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr>
                        {columns.map((col) => (
                            <th key={col} style={{ textAlign: "left", padding: "12px 16px" }}>
                                <Skeleton variant="text" width={col.length > 10 ? 90 : 60} />
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: rows }).map((_, rowIndex) => (
                        <tr key={`row-${rowIndex}`}>
                            {columns.map((col, colIndex) => (
                                <td key={`cell-${rowIndex}-${colIndex}`} style={{ padding: "12px 16px" }}>
                                    {col === "Status" ? (
                                        <Skeleton variant="rounded" width={80} height={22} />
                                    ) : col === "Product" ? (
                                        <>
                                            <Skeleton variant="text" width={120} />
                                            <Skeleton variant="text" width={80} height={14} />
                                        </>
                                    ) : col === "Address" ? (
                                        <Skeleton variant="text" width={150} />
                                    ) : (
                                        <Skeleton variant="text" width={col.length > 10 ? 90 : 60} />
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const EnquiryCardSkeleton = () => (
    <div
        style={{
            border: "1px solid #eee",
            borderRadius: 12,
            padding: 16,
            marginBottom: 12,
        }}
    >
        {/* Booking ID + Status */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <Skeleton variant="text" width={90} height={26} />
            <Skeleton variant="rounded" width={80} height={22} />
        </div>

        {/* Product */}
        <Skeleton variant="text" width="70%" />
        <Skeleton variant="text" width="45%" height={16} />

        {/* Item ID + Paid */}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
            <Skeleton variant="text" width={80} />
            <Skeleton variant="text" width={60} />
        </div>

        {/* Recipient + Phone */}
        <div style={{ marginTop: 10 }}>
            <Skeleton variant="text" width="55%" />
            <Skeleton variant="text" width="40%" />
        </div>

        {/* Address */}
        <Skeleton variant="text" width="85%" style={{ marginTop: 6 }} />

        {/* Drivers + Container */}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
            <Skeleton variant="text" width={70} />
            <Skeleton variant="text" width={70} />
            <Skeleton variant="text" width={70} />
        </div>
    </div>
);

export default EnquiryListLoader;